import {
  GridEditBooleanCell,
  GridEditModes,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

/**
 * A boolean cell only ever holds two values, so editing one is always a toggle. The built-in
 * editor instead mounts a checkbox that has to be clicked (a second interaction), then clicked
 * away from (a third) before the change is committed. Flipping and committing the value as soon
 * as edit mode starts makes the whole edit a single click, tap or Enter.
 * https://mui.com/x/react-data-grid/editing/#with-auto-stop
 * @param {import('@mui/x-data-grid-pro').GridRenderEditCellParams} params
 */
export default function ToggleBooleanEditCell({ field, id, ...params }) {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  // Row editing puts every cell of a row in edit mode at once, where auto-committing a single
  // cell would fight the rest of the row. Those grids keep the built-in checkbox.
  const isRowEditing = rootProps.editMode === GridEditModes.Row;

  useEffect(() => {
    if (isRowEditing) {
      return;
    }

    // The committed row value, not the cell's: Backspace and Delete start edit mode with a
    // cleared value, which would toggle from the wrong state.
    const toggledValue = !apiRef.current.getCellValue(id, field);

    void Promise.resolve(apiRef.current.setEditCellValue({ id, field, value: toggledValue })).then(
      isValid => {
        if (isValid) {
          apiRef.current.stopCellEditMode({ id, field });
        }
        // An invalid value keeps the cell in edit mode, where the checkbox below stays usable
      },
    );
  }, [apiRef, field, id, isRowEditing]);

  // Rendered for the frame it takes to commit, and kept around as the interactive fallback when
  // validation rejects the toggle or the grid edits whole rows
  return <GridEditBooleanCell {...params} field={field} id={id} />;
}

ToggleBooleanEditCell.propTypes = {
  field: PropTypes.string.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
